import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

// Stories data - easy to add more
// Recommended image size: 800 x 1000 pixels (4:5 portrait ratio)
const stories = [
  {
    id: 'dooz-wilsons-creek',
    slug: 'dooz-wilsons-creek',
    categories: ['DOCUMENTARY', 'COMMUNITY'],
    title: "Unsung Heroes: Dooz & The Wilsons Creek Community",
    subtitle: "A story of community resilience and the unsung heroes who stepped up when it mattered most.",
    image: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/5msjtw0d_Dooz2.jpg',
  },
  {
    id: 'mick-fanning-gold-coast',
    slug: 'mick-fanning-gold-coast',
    categories: ['FILM', 'PEOPLE'],
    title: "Unsung Heroes: Mick Fanning, Caz Goulding, Renee Honey & the Gold Coast Community",
    subtitle: "How the Gold Coast community rallied together in the face of disaster.",
    image: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/letxbtxa_Mickjpg.jpg',
  },
  {
    id: 'michael-barnes-rotorwing',
    slug: 'michael-barnes-rotorwing',
    categories: ['DOCUMENTARY', 'HEROES'],
    title: "Unsung Heroes: Michael Barnes & The Rotorwing Helicopters Volunteers",
    subtitle: "The brave pilots who risked everything to save lives from the air.",
    image: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/331ge3f9_Barnsey2.jpg',
  },
  {
    id: 'michelle-bryant',
    slug: 'michelle-bryant',
    categories: ['PEOPLE', 'ANIMALS'],
    title: "Unsung Hero: Michelle Bryant",
    subtitle: "A heart of pure compassion driven by a deep love for animals.",
    image: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/u9dwxzqg_Shelly.jpg',
  },
  {
    id: 'terry-webster-stuart-mckay',
    slug: 'terry-webster-stuart-mckay',
    categories: ['FILM', 'BEHIND THE SCENES'],
    title: "Meet The Film Makers: Terry Webster & Stuart McKay",
    subtitle: "The passion and vision behind Free Flow Films.",
    image: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/3nuo5ks5_Terry_Stu2.jpg',
  },
  {
    id: 'one-vision-productions',
    slug: 'one-vision-productions',
    categories: ['GRATITUDE', 'PARTNERS'],
    title: "The Believers: One Vision Productions",
    subtitle: "Great filmmakers recognise a great story. They recognised this one.",
    image: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/gad07q6d_OVP.jpg',
  },
  {
    id: 'felipe-baldomir',
    slug: 'felipe-baldomir',
    categories: ['MUSIC', 'COLLABORATION'],
    title: "Music Collaboration: Felipe Baldomir",
    subtitle: "Indie-Folk singer and songwriter from Uruguay taking audiences on soulful sonic journeys.",
    image: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/d9t0gxne_feleipe.jpg',
  },
  {
    id: 'rebecca-matkovic-helou',
    slug: 'rebecca-matkovic-helou',
    categories: ['FILM', 'STORYTELLING'],
    title: "Meet the Storyteller: Rebecca Matkovic-Helou",
    subtitle: "The award-winning writer who shaped the soul of Washed Away.",
    image: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/2ccdzzss_PHOTO-2026-04-01-11-13-19.jpg',
  },
];

// Story Card Component - Tall portrait style
const StoryCard = ({ story }) => {
  // Placeholder gradient if no image provided
  const placeholderStyle = !story.image ? {
    background: 'linear-gradient(180deg, #1a1a2e 0%, #2d2d44 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } : {};

  return (
    <Link 
      to={`/stories/${story.slug}`} 
      className="story-card-v2"
      data-testid={`story-card-${story.id}`}
    >
      <div className="story-card-v2-image-container" style={placeholderStyle}>
        {story.image ? (
          <img 
            src={story.image} 
            alt={story.title}
            className="story-card-v2-image"
          />
        ) : (
          <span className="story-card-placeholder-text">Image Coming Soon</span>
        )}
      </div>
      <div className="story-card-v2-content">
        <div className="story-card-v2-categories">
          {story.categories.map((cat, idx) => (
            <span key={`cat-${story.id}-${idx}`} className="story-card-v2-category">
              {cat}
              {idx < story.categories.length - 1 && <span className="category-separator">/</span>}
            </span>
          ))}
        </div>
        <h3 className="story-card-v2-title">{story.title}</h3>
        <p className="story-card-v2-subtitle">{story.subtitle}</p>
      </div>
    </Link>
  );
};

// Mobile Story Carousel Component
const MobileStoryCarousel = ({ storiesToShow }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const goToNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % storiesToShow.length);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [storiesToShow.length, isTransitioning]);

  // Auto-advance every 4 seconds
  useEffect(() => {
    const interval = setInterval(goToNext, 4000);
    return () => clearInterval(interval);
  }, [goToNext]);

  const currentStory = storiesToShow[currentIndex];

  return (
    <div className="mobile-story-carousel" data-testid="mobile-story-carousel">
      <div className="carousel-track">
        <div 
          className={`carousel-slide ${isTransitioning ? 'transitioning' : ''}`}
          key={currentStory.id}
        >
          <StoryCard story={currentStory} />
        </div>
      </div>
      
      {/* Dot indicators */}
      <div className="carousel-dots">
        {storiesToShow.map((story, idx) => (
          <button
            key={`dot-${story.id}`}
            className={`carousel-dot ${idx === currentIndex ? 'active' : ''}`}
            onClick={() => {
              if (!isTransitioning) {
                setIsTransitioning(true);
                setCurrentIndex(idx);
                setTimeout(() => setIsTransitioning(false), 500);
              }
            }}
            aria-label={`Go to story ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

// Desktop Story Carousel Component - Shows 3 at a time
const DesktopStoryCarousel = ({ storiesToShow }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const visibleCount = 3; // Show 3 cards at once
  
  const maxIndex = Math.max(0, storiesToShow.length - visibleCount);
  
  const goToNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    setTimeout(() => setIsTransitioning(false), 600);
  }, [maxIndex, isTransitioning]);
  
  const goToPrev = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
    setTimeout(() => setIsTransitioning(false), 600);
  }, [maxIndex, isTransitioning]);

  // Auto-advance every 5 seconds
  useEffect(() => {
    const interval = setInterval(goToNext, 5000);
    return () => clearInterval(interval);
  }, [goToNext]);

  return (
    <div className="desktop-story-carousel" data-testid="desktop-story-carousel">
      {/* Navigation Arrows */}
      <button 
        className="carousel-arrow carousel-arrow-left" 
        onClick={goToPrev}
        aria-label="Previous stories"
      >
        ‹
      </button>
      
      <div className="desktop-carousel-viewport">
        <div 
          className="desktop-carousel-track"
          style={{ 
            transform: `translateX(-${currentIndex * (100 / visibleCount)}%)`,
            transition: isTransitioning ? 'transform 0.6s ease' : 'none'
          }}
        >
          {storiesToShow.map((story) => (
            <div key={story.id} className="desktop-carousel-slide">
              <StoryCard story={story} />
            </div>
          ))}
        </div>
      </div>
      
      <button 
        className="carousel-arrow carousel-arrow-right" 
        onClick={goToNext}
        aria-label="Next stories"
      >
        ›
      </button>
      
      {/* Dot indicators */}
      <div className="carousel-dots desktop-carousel-dots">
        {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
          <button
            key={`desktop-dot-${idx}`}
            className={`carousel-dot ${idx === currentIndex ? 'active' : ''}`}
            onClick={() => {
              if (!isTransitioning) {
                setIsTransitioning(true);
                setCurrentIndex(idx);
                setTimeout(() => setIsTransitioning(false), 600);
              }
            }}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

// Stories Preview Section (for homepage) - Compact version
export const StoriesPreview = () => {
  return (
    <section className="stories-preview-section" data-testid="stories-preview">
      <div className="stories-preview-header">
        <h2 className="stories-preview-title">STORIES</h2>
        <Link to="/stories" className="stories-view-all">VIEW ALL</Link>
      </div>
      
      {/* Desktop: Show carousel */}
      <div className="desktop-only">
        <DesktopStoryCarousel storiesToShow={stories} />
      </div>
      
      {/* Mobile: Show carousel */}
      <div className="mobile-only">
        <MobileStoryCarousel storiesToShow={stories} />
      </div>
    </section>
  );
};

// Full Stories Page - Scrollable grid of ALL stories
const StoriesPage = () => {
  return (
    <div className="stories-page-v2" data-testid="stories-page">
      {/* Video Background */}
      <div className="video-background">
        <iframe
          src="https://player.vimeo.com/video/1084191229?background=1&autoplay=1&loop=1&byline=0&title=0&muted=1"
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
          title="Background"
        ></iframe>
        <div className="video-overlay"></div>
      </div>

      <div className="stories-page-v2-container">
        <header className="stories-page-v2-header">
          <Link to="/" className="stories-back-link">← Back to Home</Link>
          <span className="stories-page-v2-label">JOURNAL</span>
          <h1 className="stories-page-v2-title">Insights & Stories</h1>
          <p className="stories-page-v2-subtitle">
            Behind-the-scenes stories, filmmaking insights, and the unsung heroes of Australia's worst flood.
          </p>
        </header>

        {/* Static grid showing ALL stories */}
        <div className="stories-grid-v2">
          {stories.map((story) => (
            <StoryCard key={story.id} story={story} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default StoriesPage;
export { stories };
